import { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Button,
  Box,
  Icon,
  Spinner,
} from '@chakra-ui/react';
import {
  LuSparkles,
  LuList,
  LuUsers,
  LuTrophy,
  LuCrown,
  LuCheck,
  LuArchive,
  LuCalendarDays,
} from 'react-icons/lu';
import { Sparkles } from 'lucide-react';
import {
  useGetSubscriptionPlans,
  useCreateCheckoutSession,
} from '@/api/queries/useSubscriptionQueries';
import {
  type UpgradePromptContext,
  UPGRADE_PROMPT_COPY,
  SubscriptionTier,
  getTierDisplayName,
  getUpgradePromptCta,
} from '@/types/subscription';
import { useAppDispatch, useAppSelector } from '@/store/store';
import { selectCouponCode, setCheckoutLoading, setCheckoutError } from '@/store/slices/subscriptionSlice';
import { openExternalCheckout } from '@/lib/externalPurchase';
import { useIdempotencyToken } from '@/hooks/useIdempotencyToken';
import { UPGRADE_MODAL_DIGEST_FEATURE, upgradeModalDigestSubtitle } from '@/copy/digest';
import { UPGRADE_CONTEXT_COLORS, getUpgradeContextShades, type UpgradeShades } from './upgradeContextTheme';

interface UpgradePromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  context?: UpgradePromptContext;
}

const contextIcons: Record<UpgradePromptContext, React.ElementType> = {
  tasteInsights: LuSparkles,
  unlimitedLists: LuList,
  advancedVoting: LuTrophy,
  groupCapacity: LuUsers,
  issueArchive: LuArchive,
  dailyCadence: LuCalendarDays,
};

/**
 * White-on-`solid` chrome shared by the selected tier toggle and the primary
 * CTA, taken from the context's shade map instead of `colorScheme` (which
 * bypasses it: `.500` fails white text for most contexts, and Chakra flips
 * solid schemes to a pale fill with dark text in dark mode).
 */
function solidChrome(colorBase: string, shades: UpgradeShades) {
  const hoverBg = `${colorBase}.${shades.hover}`;
  return {
    bg: `${colorBase}.${shades.solid}`,
    color: 'white',
    _hover: { bg: hoverBg },
    _active: { bg: hoverBg },
  };
}

const PLUS_FEATURES = [
  'AI-powered TasteInsights generation',
  'Unlimited personal & group lists',
  'Survivor & Ranking voting modes',
  '8 members & 25 restaurants per group',
];

export function UpgradePromptModal({
  isOpen,
  onClose,
  context = 'tasteInsights',
}: UpgradePromptModalProps) {
  const dispatch = useAppDispatch();
  const couponCode = useAppSelector(selectCouponCode);
  const { data: plans, isLoading: plansLoading } = useGetSubscriptionPlans({ enabled: isOpen });
  const checkoutMutation = useCreateCheckoutSession();
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier>(SubscriptionTier.PLUS);
  const getIdempotencyToken = useIdempotencyToken();

  const content = UPGRADE_PROMPT_COPY[context];
  // The two Digest contexts lead with what the member gets (benefit-first) instead of "<cta> to unlock", and
  // put their own promise at the top of the feature list; the four legacy contexts are untouched.
  const digestFeature = context === 'dailyCadence' || context === 'issueArchive' ? UPGRADE_MODAL_DIGEST_FEATURE[context] : null;
  const features = digestFeature ? [digestFeature, ...PLUS_FEATURES] : PLUS_FEATURES;
  const cta = getUpgradePromptCta(selectedTier);
  const IconComponent = contextIcons[context];
  const colorBase = UPGRADE_CONTEXT_COLORS[context];
  const shades = getUpgradeContextShades(context);
  const solid = `${colorBase}.${shades.solid}`;
  // Accent on the CARD surface, mode-paired: a darker shade on the white card,
  // a lighter one on the dark card. The pairing lives in variables.css
  // (`.upgrade-modal`), which picks between the `-light`/`-dark` values
  // supplied on ModalContent below — no inline Chakra `_dark` logic.
  const accent = 'var(--upgrade-accent)';
  const accentProps = { color: accent };
  const outlineHover = { bg: 'var(--upgrade-accent-hover-bg)' };
  // Without an explicit `_active`, Chakra's outline variant (no `colorScheme`
  // here) presses to a pale gray fill — unreadable under dark-card accent text.
  const outlineActive = { bg: 'var(--upgrade-accent-active-bg)' };
  const outlineChrome = {
    color: accent,
    borderColor: accent,
    _hover: outlineHover,
    _active: outlineActive,
  };
  const chakraVar = (step: number) => `var(--chakra-colors-${colorBase}-${step})`;
  const accentVars = {
    '--upgrade-accent-light': chakraVar(shades.text),
    '--upgrade-accent-dark': chakraVar(shades.textDark),
    '--upgrade-accent-hover-light': chakraVar(50),
    '--upgrade-accent-active-light': chakraVar(100),
  };
  const solidStyle = solidChrome(colorBase, shades);

  // Find the monthly/yearly plans for the selected tier
  const monthlyPlan = plans?.find(
    (p) => p.tier === selectedTier && p.interval === 'month'
  );
  const yearlyPlan = plans?.find(
    (p) => p.tier === selectedTier && p.interval === 'year'
  );

  const handleUpgrade = async (stripePriceId: string) => {
    dispatch(setCheckoutLoading(true));
    dispatch(setCheckoutError(null));

    try {
      const result = await checkoutMutation.mutateAsync({
        priceId: stripePriceId,
        idempotencyToken: getIdempotencyToken(stripePriceId),
        successUrl: `${window.location.origin}/settings/subscription?success=true`,
        cancelUrl: `${window.location.origin}/settings/subscription?canceled=true`,
        coupon: couponCode || undefined,
      });

      // Open checkout in external browser for mobile, redirect for web
      await openExternalCheckout(result.checkoutUrl);
      onClose();
    } catch (error) {
      dispatch(setCheckoutError(error instanceof Error ? error.message : 'Checkout failed'));
    } finally {
      dispatch(setCheckoutLoading(false));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(8px)" />
      <ModalContent
        className="upgrade-modal"
        sx={accentVars}
        bg="var(--card-bg)"
        mx={4}
        borderRadius="var(--r-card)"
        overflow="hidden"
      >
        <ModalHeader>
          <HStack spacing={3}>
            <Box bg={`${colorBase}.100`} borderRadius="var(--r-inner)" p={2}>
              <Icon as={IconComponent} boxSize={5} color={solid} />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="lg" fontWeight="bold" color="var(--card-text)">
                {content.title}
              </Text>
              {/* `--card-text-2`, not `-3`: this 12px line is real text, and `--card-text-3` is 3.45:1 (light) / 3.87:1 (dark) on the modal's `--card-bg`, under the 4.5:1 AA floor. */}
              <Text fontSize="xs" color="var(--card-text-2)" fontWeight="normal">
                {digestFeature ? upgradeModalDigestSubtitle(getTierDisplayName(selectedTier)) : `${cta} to unlock`}
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={4}>
          <VStack spacing={4}>
            {/* Premium Feature Banner */}
            <Box
              bg={`linear-gradient(135deg, var(--chakra-colors-${colorBase}-${shades.solid}), var(--chakra-colors-purple-500))`}
              borderRadius="var(--r-card-compact)"
              p={4}
              position="relative"
              overflow="hidden"
              w="100%"
            >
              <Box position="absolute" top={-20} right={-20} opacity={0.2}>
                <Sparkles size={80} color="white" />
              </Box>
              <HStack spacing={3} position="relative" zIndex={1}>
                <Icon as={LuCrown} boxSize={6} color="white" />
                <VStack align="start" spacing={0}>
                  <Text color="white" fontWeight="bold" fontSize="sm">
                    {getTierDisplayName(selectedTier)}
                  </Text>
                  {/* TEXT-COLOR-LITERAL: white-alpha price line on the banner's saturated accent-to-purple gradient (never a card surface); contrast pinned per context in src/types/__tests__/upgradePromptCopy.test.ts */}
                  <Text color="whiteAlpha.900" fontSize="xs">
                    {monthlyPlan
                      ? `$${monthlyPlan.priceUsd}/month`
                      : 'Premium features'}
                  </Text>
                </VStack>
              </HStack>
            </Box>

            {/* Tier toggle */}
            <HStack spacing={2} w="100%">
              <Button
                size="sm"
                flex={1}
                variant={selectedTier === SubscriptionTier.PLUS ? 'solid' : 'outline'}
                {...(selectedTier === SubscriptionTier.PLUS ? solidStyle : outlineChrome)}
                onClick={() => setSelectedTier(SubscriptionTier.PLUS)}
              >
                Paire+
              </Button>
              <Button
                size="sm"
                flex={1}
                variant={selectedTier === SubscriptionTier.PRO ? 'solid' : 'outline'}
                {...(selectedTier === SubscriptionTier.PRO ? solidStyle : outlineChrome)}
                onClick={() => setSelectedTier(SubscriptionTier.PRO)}
              >
                Pro
              </Button>
            </HStack>

            {/* Description */}
            <Text
              fontSize="sm"
              color="var(--card-text-2)"
              textAlign="center"
              lineHeight="tall"
            >
              {content.description}
            </Text>

            {/* Feature list */}
            <Box w="100%">
              <Text fontSize="xs" fontWeight="medium" color="var(--card-text-3)" mb={2}>
                PAIRE+ AND PRO INCLUDE:
              </Text>
              <VStack spacing={2} align="stretch">
                {features.map((feature, i) => (
                  <HStack key={i} spacing={2}>
                    <Icon as={LuCheck} boxSize={4} {...accentProps} />
                    <Text fontSize="sm" color="var(--card-text-2)">
                      {feature}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </Box>

            {/* Yearly savings callout */}
            {yearlyPlan && monthlyPlan && (
              <Box
                bg="green.50"
                borderRadius="var(--r-inner)"
                px={3}
                py={2}
                w="100%"
                borderWidth="1px"
                borderColor="green.200"
              >
                <HStack justify="space-between">
                  <Text fontSize="sm" color="green.700" fontWeight="medium">
                    Save 33% with yearly
                  </Text>
                  <Text fontSize="sm" color="green.700">
                    ${yearlyPlan.priceUsd}/year
                  </Text>
                </HStack>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="var(--card-divider)" pt={4}>
          <VStack spacing={3} width="100%">
            {plansLoading ? (
              <Spinner size="md" {...accentProps} />
            ) : (
              <>
                {monthlyPlan && (
                  <Button
                    width="100%"
                    size="lg"
                    {...solidStyle}
                    onClick={() => handleUpgrade(monthlyPlan.stripePriceId)}
                    isLoading={checkoutMutation.isPending}
                    leftIcon={<Icon as={LuCrown} />}
                  >
                    {cta} - ${monthlyPlan.priceUsd}/mo
                  </Button>
                )}

                {yearlyPlan && (
                  <Button
                    width="100%"
                    size="md"
                    variant="outline"
                    borderColor={`${colorBase}.300`}
                    {...accentProps}
                    _hover={outlineHover}
                    _active={outlineActive}
                    onClick={() => handleUpgrade(yearlyPlan.stripePriceId)}
                    isLoading={checkoutMutation.isPending}
                  >
                    Get yearly (save 33%)
                  </Button>
                )}

                <Text fontSize="xs" color="var(--card-text-3)" textAlign="center">
                  Cancel anytime. No commitment.
                </Text>
              </>
            )}
          </VStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
